// Utility to validate GCECT roll numbers and parse them.
export type RollParsed = {
  degree: "B" | "M";
  year: number; // full year, e.g. 2023
  courseCode: number; // numeric representation of course/branch code
  rollNumber: string; // remaining roll number part
};

export function validateRollNumber(roll: string): {
  valid: boolean;
  error?: string;
  parsed?: RollParsed;
} {
  if (!roll || String(roll).trim().length === 0) {
    return { valid: false, error: "Empty roll number" };
  }

  const s = String(roll).trim();
  const nowYear = new Date().getFullYear();

  // Pattern like: GCECTB-R23-3020 or GCECTB-R23-3-020 or simplified B-R23-3020
  // We'll accept flexible separators and optional prefix.

  // Normalize common prefix GCECTB / GCECTM
  const prefMatch = s.match(/^\s*(GCECT)?([BMbm])[-_\s]*/);
  let core = s;
  let degree: "B" | "M" | null = null;
  if (prefMatch) {
    degree = prefMatch[2].toUpperCase() === "B" ? "B" : "M";
    core = s.slice(prefMatch[0].length);
  }

  // Accept forms like: R23-3020 or L23-3020 (R = regular, L = lateral)
  const rMatch = core.match(
    /^(?:[RL])[-_\s]?(\d{2,4})[-_\s]*(\d{1,4})[-_\s]*(\d{1,6})?$/i
  );
  if (rMatch) {
    const yearStr = rMatch[1];
    const courseStr = rMatch[2];
    let rollNum = rMatch[3] || "";
    const year =
      yearStr.length === 2 ? 2000 + Number(yearStr) : Number(yearStr);
    if (Number.isNaN(year) || year < 2000 || year > nowYear + 1) {
      return { valid: false, error: `Invalid admitted year: ${yearStr}` };
    }
    // courseStr might contain both course code and trailing roll digits (e.g. "3020" meaning course 3 and roll 020)
    let courseCode = Number(courseStr);
    if (
      (Number.isNaN(courseCode) || String(courseStr).length > 1) &&
      !rollNum
    ) {
      // try splitting: first digit is course code, rest is roll number
      if (/^\d+$/.test(courseStr) && String(courseStr).length > 1) {
        const candidate = Number(String(courseStr).charAt(0));
        const rest = String(courseStr).slice(1);
        if ([1, 2, 3].includes(candidate) && rest.length > 0) {
          courseCode = candidate;
          rollNum = rest;
        }
      }
    }

    // Only allow course codes 1,2,3
    if (![1, 2, 3].includes(courseCode)) {
      return { valid: false, error: `Course code must be 1, 2 or 3` };
    }
    if (!rollNum || String(rollNum).trim().length === 0) {
      return {
        valid: false,
        error: `Full roll number required (missing trailing digits)`,
      };
    }
    if (!degree) {
      // If degree not present in prefix, try to infer by course code length (not reliable)
      degree = "B";
    }
    return {
      valid: true,
      parsed: { degree, year, courseCode, rollNumber: rollNum },
    };
  }

  // Compact forms are intentionally not supported to avoid ambiguous parsing.
  // Examples like `B23-3020` or `B23C3020` are rejected; use explicit forms
  // like `GCECTB-R23-3020` or `B-R23-3020` instead.

  // As a last attempt, accept formats like GCECTB-R23-3020 explicitly
  const explicit = s.match(
    /^\s*GCECT([BMbm])[-_\s]*[-RrLl]?([0-9]{2,4})[-_\s]*([0-9]{1,4})[-_\s]*([0-9]{1,6})\s*$/i
  );
  if (explicit) {
    const deg = explicit[1].toUpperCase() === "B" ? "B" : "M";
    const y = explicit[2];
    const courseStr = explicit[3];
    const rollnum = explicit[4];
    const year = y.length === 2 ? 2000 + Number(y) : Number(y);
    if (Number.isNaN(year) || year < 2000 || year > nowYear + 1) {
      return { valid: false, error: `Invalid admitted year: ${y}` };
    }
    const courseCode = Number(courseStr);
    if (Number.isNaN(courseCode) || ![1, 2, 3].includes(courseCode)) {
      return { valid: false, error: `Course code must be 1, 2 or 3` };
    }
    if (!rollnum || String(rollnum).trim().length === 0) {
      return {
        valid: false,
        error: `Full roll number required (missing trailing digits)`,
      };
    }
    return {
      valid: true,
      parsed: {
        degree: deg as "B" | "M",
        year,
        courseCode,
        rollNumber: rollnum,
      },
    };
  }

  return { valid: false, error: "Unrecognized roll number format" };
}
